import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Search, 
  Users, 
  CreditCard, 
  Flag, 
  TrendingUp,
  Plus,
  Minus,
  Shield,
  Activity,
  Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AdminJobIngest } from "@/components/AdminJobIngest";

interface User {
  id: string;
  email: string;
  full_name?: string;
  created_at: string;
  plan_type?: string;
  total_credits?: number;
}

interface FeatureFlag {
  id: string;
  flag_key: string;
  enabled: boolean;
  description: string;
}

interface AdminStats {
  totalUsers: number;
  activeSubscriptions: number;
  totalCreditsIssued: number;
  revenueThisMonth: number;
}

export default function Admin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [adjustingCredits, setAdjustingCredits] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Use secure has_role function
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });

      if (error || !data) {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have admin access",
        });
        navigate('/dashboard');
        return;
      }

      setIsAdmin(true);
      fetchAdminData();
    } catch (error) {
      console.error('Admin check error:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminData = async () => {
    await Promise.all([
      fetchUsers(),
      fetchFeatureFlags(),
      fetchStats(),
    ]);
  };

  const fetchUsers = async () => {
    try {
      // Get users with their credits
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (profilesError) throw profilesError;

      const userIds = profilesData?.map(p => p.id) || [];
      
      const { data: creditsData } = await supabase
        .from('credits')
        .select('user_id, total_credits, plan_type')
        .in('user_id', userIds);

      const creditsMap = new Map(creditsData?.map(c => [c.user_id, c]) || []);

      const enrichedUsers = profilesData?.map(profile => ({
        ...profile,
        plan_type: creditsMap.get(profile.id)?.plan_type || 'free',
        total_credits: creditsMap.get(profile.id)?.total_credits || 0,
      })) || [];

      setUsers(enrichedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('flag_key');

      if (error) throw error;
      setFeatureFlags(data || []);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Total users
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Active subscriptions
      const { count: subCount } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .in('status', ['active', 'trialing']);

      // Total credits issued
      const { data: creditsData } = await supabase
        .from('credit_transactions')
        .select('credits_used')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      const totalCredits = creditsData?.reduce((sum, t) => sum + Math.abs(t.credits_used), 0) || 0;

      setStats({
        totalUsers: userCount || 0,
        activeSubscriptions: subCount || 0,
        totalCreditsIssued: totalCredits,
        revenueThisMonth: 0, // Would need Stripe integration
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const adjustCredits = async (userId: string, amount: number) => {
    setAdjustingCredits(userId);
    try {
      const { data: currentCredits } = await supabase
        .from('credits')
        .select('total_credits')
        .eq('user_id', userId)
        .single();

      if (!currentCredits) {
        throw new Error('User credits not found');
      }

      const newTotal = Math.max(0, currentCredits.total_credits + amount);

      const { error } = await supabase
        .from('credits')
        .update({ total_credits: newTotal })
        .eq('user_id', userId);

      if (error) throw error;

      // Log transaction
      await supabase.from('credit_transactions').insert({
        user_id: userId,
        document_type: 'admin_adjustment',
        action: 'adjustment',
        credits_used: -amount,
        credits_remaining_after: newTotal,
        notes: `Admin adjustment: ${amount > 0 ? '+' : ''}${amount} credits`,
      });

      toast({
        title: "Credits adjusted",
        description: `${amount > 0 ? 'Added' : 'Removed'} ${Math.abs(amount)} credits`,
      });

      fetchUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to adjust credits",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setAdjustingCredits(null);
    }
  };

  const toggleFeatureFlag = async (flagId: string, enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ enabled })
        .eq('id', flagId);

      if (error) throw error;

      toast({
        title: enabled ? "Feature enabled" : "Feature disabled",
      });

      fetchFeatureFlags();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to update feature flag",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Activity className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Verifying admin access...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <ErrorState error="Access denied. Admin privileges required." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          </div>
          <p className="text-muted-foreground">
            Manage users, credits, and system settings
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.totalUsers}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Active Subs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <span className="text-2xl font-bold">{stats.activeSubscriptions}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Credits (30d)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.totalCreditsIssued}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Feature Flags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Flag className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">
                    {featureFlags.filter(f => f.enabled).length}/{featureFlags.length}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="features">Feature Flags</TabsTrigger>
            <TabsTrigger value="jobs">Job Ingestion</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Search users, view details, and manage credits
                </CardDescription>
                <div className="pt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search by email or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredUsers.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No users found"
                    description="Try adjusting your search"
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{user.full_name || 'N/A'}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                user.plan_type === 'premium'
                                  ? 'default'
                                  : user.plan_type === 'pro'
                                  ? 'secondary'
                                  : 'outline'
                              }
                            >
                              {user.plan_type || 'free'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono">{user.total_credits || 0}</span>
                          </TableCell>
                          <TableCell>
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => adjustCredits(user.id, 10)}
                                disabled={adjustingCredits === user.id}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                10
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => adjustCredits(user.id, -10)}
                                disabled={adjustingCredits === user.id}
                              >
                                <Minus className="h-3 w-3 mr-1" />
                                10
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="features" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Feature Flags</CardTitle>
                <CardDescription>
                  Control which features are enabled globally
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {featureFlags.map((flag) => (
                  <div
                    key={flag.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <Label htmlFor={flag.flag_key} className="text-base font-medium cursor-pointer">
                        {flag.flag_key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Label>
                      {flag.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {flag.description}
                        </p>
                      )}
                    </div>
                    <Switch
                      id={flag.flag_key}
                      checked={flag.enabled}
                      onCheckedChange={(checked) => toggleFeatureFlag(flag.id, checked)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Job Ingestion Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>LinkedIn Job Ingestion</CardTitle>
                <CardDescription>
                  Manually trigger Apify LinkedIn scraper to ingest Easy Apply jobs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminJobIngest />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
